import Image from "next/image";

import PeopleFilledIcon from "@/icons/__generated__/PeopleFilled/PeopleFilled16";
import { IAttachment, IListingSaveResponse, IProject } from "~/types/api-types";
import { ISelectedProjects } from "~/types/local-types";

export const only1projectSaved = (
  projects: IProject[],
  selectedProjects: ISelectedProjects,
) => {
  const allProjects = projects;
  const projectIDs = allProjects.map((project) => project._id);
  const listingSavedToTheseProjects = projectIDs.filter(
    (projectId) =>
      selectedProjects[projectId] &&
      selectedProjects[projectId].action !== "delete",
  );

  return {
    count: listingSavedToTheseProjects.length,
    projectIDs: listingSavedToTheseProjects,
  };
};

export const listingSavedToOnly1Board = (
  changeModal: (modal: string) => void,
) => {
  changeModal("share");
};

export const listSaveCallback = (
  res: IListingSaveResponse,
  setAttachments: (callback: (prev: IAttachment[]) => IAttachment[]) => void,
) => {
  setAttachments((prevAttachments) => {
    // 1. filter out attachments that were deleted
    const arrayOfDeletedIds = res.responses
      .filter((a) => a.message === "attachment deleted")
      .map((a) => a._id);

    const updatedAttachments = prevAttachments.filter(
      (a) => !arrayOfDeletedIds.includes(a._id),
    );

    // 2. Build a map of new attachments from the response
    const attachmentById = res.responses.reduce<Record<string, IAttachment>>(
      (result, value) => {
        if (value.attachment) {
          result[value.attachment._id] = value.attachment;
        }
        return result;
      },
      {},
    );

    // 3. Filter out attachments that already exist in the state
    const newAttachments = updatedAttachments.filter(
      (attachment) => !attachmentById[attachment._id],
    );

    // 4. Add new "Success" attachments at the start
    const successAttachments = Object.values(attachmentById).filter(({ _id }) =>
      res.responses.find(
        (response) =>
          response.attachment?._id === _id && response.message === "Success",
      ),
    );

    return [...successAttachments, ...newAttachments];
  });
};

export const generateImageSrcSet = (externalImageId: string) => {
  const baseUrl =
    "https://img.peerspace.com/image/upload/c_crop,g_custom/g_auto,c_fill,q_auto,f_auto,fl_progressive:steep";
  const sizes = [
    { width: 512, height: 303 },
    { width: 768, height: 455 },
  ];

  const srcSet = sizes
    .map(
      ({ width, height }) =>
        `${baseUrl},dpr_1,w_${width},h_${height}/${externalImageId} ${width}w`,
    )
    .join(",");

  const defaultSrc = `${baseUrl.replace("q_auto", "q_auto:eco")},w_512,h_303/${externalImageId}`;

  return {
    srcSet,
    defaultSrc,
    sizes: "50vw",
  };
};

export const getListingUI = (
  listingImg: string,
  listingTitle: string,
  listingLocation: string,
  listingCapacity: number,
) => {
  return (
    <div className="tw-space-y-16">
      <Image
        src={generateImageSrcSet(listingImg).defaultSrc}
        fill
        alt="Property"
        className="tw-w-full tw-rounded-4"
        unoptimized
      />
      <div>
        <h2 className="tw-text-24 tw-font-bold">{listingTitle}</h2>
        <p className="tw-text-gray-600">
          {listingLocation} • {listingCapacity}
        </p>
      </div>
    </div>
  );
};

const loadGoogleMaps = async (googleMapsApiKey: string) => {
  const { Loader } = await import("@googlemaps/js-api-loader");

  const loader = new Loader({
    apiKey: googleMapsApiKey,
    libraries: ["places"],
  });

  const google = await loader.load();
  return google;
};

export const autoComplete = async (
  input: string,
  setPredictions: (
    predictions: google.maps.places.AutocompletePrediction[],
  ) => void,
  setNoResults: (noResults: boolean) => void,
  googleMapsApiKey: string,
): Promise<void> => {
  try {
    const google = await loadGoogleMaps(googleMapsApiKey);

    // Check if AutocompleteService is available
    if (!google?.maps?.places) {
      throw new Error("Google Maps Places API is not loaded.");
    }

    const completer = new google.maps.places.AutocompleteService();

    const options = {
      input,
      types: ["geocode"], // Specify the type of predictions
    };

    completer.getPlacePredictions(
      options,
      (
        predictions: google.maps.places.AutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatus,
      ) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          setNoResults(true);
          setPredictions([]);
          return;
        }

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setNoResults(false);
          setPredictions(
            predictions.map((p) => ({
              ...p,
              reference: p.place_id,
            })),
          );
        }
      },
    );
  } catch (error) {
    setNoResults(true); // Handle errors by showing no results
    throw new Error(`Error in autoComplete: ${error}`);
  }
};

export const listingDisplay = ({
  listingImg,
  listingTitle,
  listingLocation,
  listingCapacity,
}: {
  listingImg: string;
  listingTitle: string;
  listingLocation: string;
  listingCapacity: number;
}) => {
  return (
    <div className="tw-hidden md:tw-block tw-w-5/12 tw-h-full tw-p-24 tw-border-r-[1px] tw-border-gray-200">
      <div className="tw-space-y-16">
        <div className="tw-relative tw-w-full tw-h-[140px]">
          <Image
            src={generateImageSrcSet(listingImg).defaultSrc}
            fill
            alt="Property"
            className="tw-w-full tw-rounded-4 tw-object-cover"
            unoptimized
          />
        </div>
        <div>
          <h2 className="tw-text-24 tw-font-bold">{listingTitle}</h2>
          <p className="tw-text-gray-600 tw-flex tw-items-center tw-gap-8">
            {listingLocation}
            <span className="tw-flex tw-items-center">
              <PeopleFilledIcon />
              {listingCapacity}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};
